import  { useState, useEffect, useContext } from "react";
import {
  Table,
  Form,
  Input,
  Button,
  Modal,
  Space,
  message,
  Tooltip,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import { AuthContext } from "../../contexts/AuthContext";

const REACT_APP_BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

const Regions = () => {
  const [regions, setRegions] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState<"region" | "district" | "ward">("region");
  const [form] = Form.useForm();
  const access = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      const response = await fetch(`${REACT_APP_BASE_API_URL}/v1/regions`);
      const data = await response.json();
      setRegions(data);
    } catch (error) {
      message.error("Failed to fetch regions.");
    }
  };

  const showModal = (
    type: "region" | "district" | "ward",
    regionId: string | null = null,
    districtId: string | null = null,
    record: any = null
  ) => {

    setModalType(type);
    setSelectedRegion(regionId);
    setSelectedDistrict(districtId);
    setSelectedRecord(record);

    if (record) {
      form.setFieldsValue(record);
    } else {
      form.resetFields();
    }

    setIsModalVisible(true);
  };

  const handleAddOrEdit = async (values: any) => {
    try {
      let url = "";
      let method = "POST";

      if (modalType === "region") {
        url = "/v1/regions";
        if (selectedRecord?._id) {
          url += `/${selectedRecord?._id}`;
          method = "PUT";
        }
      } else if (modalType === "district") {
        if (!selectedRegion) {
          message.error("No region selected");
          return;
        }
        url = `/v1/regions/${selectedRegion}/districts`;
        if (selectedRecord?._id) {
          url += `/${selectedRecord?._id}`;
          method = "PUT";
        }
      } else if (modalType === "ward") {
        if (!selectedRegion || !selectedDistrict) {
          message.error("No region or district selected");
          return;
        }
        url = `/v1/regions/${selectedRegion}/districts/${selectedDistrict}/wards`;
        if (selectedRecord?._id) {
          url += `/${selectedRecord?._id}`;
          method = "PUT";
        }
      }

      const response = await fetch(`${REACT_APP_BASE_API_URL}${url}`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        fetchRegions();
        message.success("Operation successful.");
        setIsModalVisible(false);
      } else {
        message.error("Operation failed.");
      }
    } catch (error) {
      message.error("Error occurred.");
    }
  };

  const handleDelete = async (
    type: string,
    id: string,
    regionId?: string,
    districtId?: string
  ) => {
    try {
      let url = "";
      if (type === "region") {
        url = `/v1/regions/${id}`;
      } else if (type === "district") {
        if (!regionId) {
          message.error("No region selected");
          return;
        }
        url = `/v1/regions/${regionId}/districts/${id}`;
      } else if (type === "ward") {
        if (!regionId || !districtId) {
          message.error("No region or district selected");
          return;
        }
        url = `/v1/regions/${regionId}/districts/${districtId}/wards/${id}`;
      }

      const response = await fetch(`${REACT_APP_BASE_API_URL}${url}`, {
        method: "DELETE",
      });
      if (response.ok) {
        fetchRegions();
        message.success("Deleted successfully.");
      } else {
        message.error("Failed to delete.");
      }
    } catch (error) {
      message.error("Error occurred while deleting.");
    }
  };

  // Columns for Region level
  const regionColumns: any = [
    {
      title: "Region Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal("region", null, null, record)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete("region", record?._id)}
          >
            Delete
          </Button>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => showModal("district", record?._id)}
          >
            Add District
          </Button>
        </Space>
      ),
    },
  ];

  // Columns for District level
  const districtColumns: any = [
    {
      title: "District Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal("district", record.regionId, null, record)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete("district", record?._id, record.regionId)}
          >
            Delete
          </Button>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => showModal("ward", record.regionId, record?._id)}
          >
            Add Ward
          </Button>
        </Space>
      ),
    },
  ];
  

  // Columns for Ward level
  const wardColumns: any = [
    {
      title: "Ward Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() =>
              showModal("ward", record.regionId, record.districtId, record)
            }
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              handleDelete("ward", record._id, record.regionId, record.districtId)
            }
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];
  

  return (
    <div className="bg-gray-100">
    <div className="bg-white m-1">
      <div className="flex flex-col sm:flex-row justify-between items-center border px-2">
        <div className="">
          <div className="font-bold capitalize text-lg">
            All Regions
          </div>
          <div className="text-gray-600 mb-1">Manage all regions</div>
        </div>

        <div className="flex">

          {access?.includes(user?.accessLevel) && (
            <button
            onClick={() => showModal("region")}
              className="flex mx-2 justify-center items-center border px-4 h-8  bg-karimjee  hover:bg-karimjee-dark text-white shadow-sm"
            >
              + New Region
            </button>
          )}
        </div>
      </div>

        <div className="border p-2 shadow-sm">
        <div className="">


      <Table
        dataSource={regions}
        columns={regionColumns}
        rowKey={(record) => record?._id || Math.random()}
        expandable={{
          expandedRowRender: (region: any) => (
            <Table
              dataSource={
                (region?.districts ?? []).map((district: any) => ({
                  ...district,
                  regionId: region?._id,
                })) || []
              }
              columns={districtColumns}
              rowKey={(record) => record?._id || Math.random()}
              pagination={false}
              expandable={{
                expandedRowRender: (district: any) => (
                  <Table
                    dataSource={
                      (district?.wards ?? [])
                        .filter((ward: any) => ward?._id)
                        .map((ward: any) => ({
                          ...ward,
                          regionId: region?._id,
                          districtId: district?._id,
                        })) || []
                    }
                    columns={wardColumns}
                    rowKey={(record) => record?._id || Math.random()}
                    pagination={false}
                  />
                ),
              }}
            />
          ),
        }}
      />

      <Modal
        title={
          modalType === "region"
            ? selectedRecord ? "Edit Region" : "Add Region"
            : modalType === "district"
            ? selectedRecord ? "Edit District" : "Add District"
            : selectedRecord ? "Edit Ward" : "Add Ward"
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => {
          form
            .validateFields()
            .then((values) => {
              handleAddOrEdit(values);
            })
            .catch(() => {});
        }}
      >
        <Form form={form} layout="vertical" initialValues={selectedRecord || {}}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter the name." }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>

    </div>
    </div>
    </div>
  );
};

export default Regions;
